import { SEOHead, BreadcrumbNav } from '@/components/feature/SEO';
import { aboutInfo } from '@/mocks/about';

export default function About() {
  const breadcrumbs = [
    { name: '首页', url: '/' },
    { name: '关于我们', url: '/about' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于制服诱惑',
    description: aboutInfo.intro,
    url: 'https://dressplay.github.io/about',
  };

  return (
    <main className="pt-20">
      <SEOHead
        title="关于我们"
        description={aboutInfo.intro}
        keywords="制服诱惑,关于我们,制服文化,制服时尚,品牌介绍"
        canonicalUrl="https://dressplay.github.io/about"
        breadcrumbs={breadcrumbs}
        schema={aboutSchema}
      />

      <BreadcrumbNav items={breadcrumbs} />

      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20design%20studio%20workspace%20with%20fabric%20swatches%20and%20design%20sketches%2C%20warm%20golden%20lighting%2C%20creative%20atmosphere%2C%20clean%20cream%20aesthetic%2C%20editorial%20photography%20with%20soft%20shadows&width=1600&height=900&seq=about-hero-01&orientation=landscape"
          alt="关于制服诱惑"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
            关于我们
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-xl">
            {aboutInfo.tagline}
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            品牌介绍
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
            {aboutInfo.intro}
          </p>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            {aboutInfo.mission}
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-10 text-center">
            发展历程
          </h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2 hidden md:block"></div>
            <div className="space-y-8">
              {aboutInfo.history.map((item, index) => (
                <div
                  key={index}
                  className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-background-50 rounded-lg p-5 md:p-6">
                      <span className="inline-block text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-2">
                        {item.year}
                      </span>
                      <p className="text-sm text-foreground-700">{item.event}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-8 flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-accent-500"></div>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-10 text-center">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {aboutInfo.testimonials.map((item, index) => (
              <div
                key={index}
                className="bg-background-100 rounded-lg p-6"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                  {Array.from({ length: 5 - item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-line text-foreground-300 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{item.content}&rdquo;
                </p>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{item.name}</p>
                  <p className="text-xs text-foreground-500">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-10 text-center">
            联系我们
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
            <div className="bg-background-50 rounded-lg p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-mail-line text-accent-600"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.email}</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-wechat-line text-accent-600"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">微信公众号</p>
                <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.wechat}</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-weibo-line text-accent-600"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">微博</p>
                <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.weibo}</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-map-pin-line text-accent-600"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">地址</p>
                <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.address}</p>
              </div>
            </div>
          </div>

          <div className="bg-background-50 rounded-lg p-6 md:p-8">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">
              发送消息
            </h3>
            <form
              data-readdy-form="true"
              action="https://readdy.ai/api/form/d9u4tklca23r0tr1aql0"
              method="POST"
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  消息
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入消息内容（不超过500字）"
                ></textarea>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-antispam"
              />
              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                发送消息
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}